/**
 * Generates deterministic, varied gradient backgrounds for communities
 * Same community name will always produce the same gradient
 */

// Color palettes that work well together
const COLOR_PALETTES = [
  // Warm sunset
  ['#ff6b6b', '#feca57', '#ff9ff3'],
  // Cool ocean  
  ['#74b9ff', '#0984e3', '#6c5ce7'],
  // Forest vibes
  ['#00b894', '#55a3ff', '#a29bfe'],
  // Purple dreams
  ['#fd79a8', '#fdcb6e', '#e84393'],
  // Cosmic
  ['#636e72', '#74b9ff', '#fd79a8'],
  // Earth tones
  ['#e17055', '#fdcb6e', '#00b894'],
  // Neon
  ['#00cec9', '#6c5ce7', '#fd79a8'],
  // Autumn
  ['#e17055', '#fdcb6e', '#e84393'],
  // Deep sea
  ['#0984e3', '#00b894', '#74b9ff'],
  // Fire
  ['#ff6b6b', '#fd79a8', '#fdcb6e']
];

// Gradient pattern types
const GRADIENT_PATTERNS = [
  'linear-gradient(135deg, {0}, {1})',
  'linear-gradient(45deg, {0}, {1}, {2})',
  'radial-gradient(circle at 20% 50%, {0}, {1})',
  'radial-gradient(ellipse at 70% 30%, {0}, {1}, {2})',
  'conic-gradient(from 45deg, {0}, {1}, {0})',
  'linear-gradient(90deg, {0} 0%, {1} 50%, {2} 100%)',
  'radial-gradient(circle at top left, {0}, {1})',
  'linear-gradient(120deg, {0} 0%, {1} 60%, {2} 100%)'
];

// Noise/texture overlay patterns
const NOISE_OVERLAYS = [
  // Subtle dot pattern
  `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
  // Diagonal lines
  `repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)`,
  // Grid pattern
  `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
  // Organic texture
  `radial-gradient(circle at 20% 80%, rgba(0,0,0,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
  // Film grain
  `repeating-linear-gradient(0deg, transparent, rgba(255,255,255,0.05) 1px, transparent 2px), repeating-linear-gradient(90deg, transparent, rgba(0,0,0,0.05) 1px, transparent 2px)`
];

/**
 * Simple hash function to convert string to number
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get deterministic "random" values based on seed
 */
function seededRandom(seed: number, index: number = 0): number {
  const x = Math.sin(seed + index) * 10000;
  return x - Math.floor(x);
}

/**
 * Generates a unique, deterministic gradient for a community name
 */
export function generateRandomGradient(communityName: string): string {
  const seed = hashString(communityName.toLowerCase());
  
  // Select palette
  const paletteIndex = Math.floor(seededRandom(seed, 0) * COLOR_PALETTES.length);
  const palette = COLOR_PALETTES[paletteIndex];
  
  // Select pattern
  const patternIndex = Math.floor(seededRandom(seed, 1) * GRADIENT_PATTERNS.length);
  const pattern = GRADIENT_PATTERNS[patternIndex];
  
  // Select noise overlay
  const noiseIndex = Math.floor(seededRandom(seed, 2) * NOISE_OVERLAYS.length);
  const noiseOverlay = NOISE_OVERLAYS[noiseIndex];
  
  // Fill pattern with colors
  let gradient = pattern;
  palette.forEach((color, index) => {
    gradient = gradient.replace(`{${index}}`, color);
  });
  
  // Combine gradient with noise overlay
  const combinedGradient = `${noiseOverlay}, ${gradient}`;
  
  return combinedGradient;
}

/**
 * Generates a simpler gradient for fallback cases
 */
export function generateSimpleGradient(communityName: string): string {
  const seed = hashString(communityName.toLowerCase());
  const paletteIndex = Math.floor(seededRandom(seed, 0) * COLOR_PALETTES.length);
  const palette = COLOR_PALETTES[paletteIndex];
  
  return `linear-gradient(135deg, ${palette[0]}, ${palette[1] || palette[0]})`;
}

/**
 * Get a deterministic primary color from community name (for backwards compatibility)
 */
export function generateColorFromString(str: string): string {
  const seed = hashString(str.toLowerCase());
  const paletteIndex = Math.floor(seededRandom(seed, 0) * COLOR_PALETTES.length);
  return COLOR_PALETTES[paletteIndex][0];
}